import { useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useCompanyCore } from '../company';
import { priceSuggestionSelector } from './store/selector';
import { priceSuggestionApiService } from './services/api';
import {
    priceSuggestionInitAction,
    priceSuggestionSuccessAction,
    priceSuggestionFailureAction,
    clearPriceSuggestionAction,
} from './store/reducer';

export function usePriceSuggestionCore() {
    const priceSuggestionState = useSelector(priceSuggestionSelector);
    const dispatch = useDispatch();
    const { companyId } = useCompanyCore();

    const getSuggestedPrice = useCallback(
        async (
            productName: string,
            onSuccess?: (price: number) => void,
            onFailure?: () => void
        ) => {
            if (!productName.trim()) {
                dispatch(priceSuggestionFailureAction({
                    errorCode: 400,
                    message: 'La descripción del producto es necesaria para obtener el precio sugerido'
                }));
                onFailure?.();
                return;
            }

            if (!companyId) {
                dispatch(priceSuggestionFailureAction({
                    errorCode: 400,
                    message: 'ID de empresa no disponible'
                }));
                onFailure?.();
                return;
            }

            try {
                dispatch(priceSuggestionInitAction());
                const response = await priceSuggestionApiService.getSuggestedPrice(companyId, productName.trim());

                console.log('Respuesta de la API:', response);

                // Validar que la respuesta tenga el formato esperado
                if (!response || typeof response.suggestedPrice !== 'number' || isNaN(response.suggestedPrice) || response.suggestedPrice <= 0) {
                    console.error('Respuesta inválida de precio sugerido:', response);
                    dispatch(priceSuggestionFailureAction({
                        errorCode: 400,
                        message: `Precio inválido recibido: ${response?.suggestedPrice || 'undefined'}`
                    }));
                    onFailure?.();
                    return;
                }

                dispatch(priceSuggestionSuccessAction(response));
                onSuccess?.(response.suggestedPrice);
            } catch (error: any) {
                console.error('Error obteniendo precio sugerido:', error);
                dispatch(priceSuggestionFailureAction({
                    errorCode: error.request?.status || 500,
                    message: 'No se pudo obtener el precio sugerido. Intenta nuevamente.'
                }));
                onFailure?.();
            }
        },
        [dispatch, companyId]
    );

    const clearSuggestion = useCallback(() => {
        dispatch(clearPriceSuggestionAction());
    }, [dispatch]);

    return {
        // Estado
        suggestedPrice: priceSuggestionState.suggestedPrice,
        loading: priceSuggestionState.loading,
        error: priceSuggestionState.error,

        // Métodos
        getSuggestedPrice,
        clearSuggestion,
    };
}

// Función utilitaria para formatear precios
export const formatPrice = (price: number): string => {
    return new Intl.NumberFormat('es-CO', {
        style: 'currency',
        currency: 'COP',
        minimumFractionDigits: 0,
        maximumFractionDigits: 0,
    }).format(price);
}; 